<template>
  <v-dialog
    v-model="showUploadDialog"
    persistent
    :fullscreen="
      !uploadingStatus && completeUpload === false && showRetryDialog
    "
    max-width="800"
  >
    <!-- Uploading spinner -->
    <div
      class="d-flex flex-column justify-center align-center blue--text py-15 white"
      style="width: 100%"
      v-if="uploadingStatus"
    >
      <div class="text-center">
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </div>
      <h3 class="mt-6">Uploading your files...</h3>
    </div>

    <!-- Completed dialog -->
    <v-card
      class="d-flex flex-column justify-center align-center py-15 px-10"
      style="width: 100%"
      v-else-if="!uploadingStatus && completeUpload"
    >
      <div class="green--text">
        <b-icon-check-circle-fill
          style="width: 40px; height: 40px;"
        ></b-icon-check-circle-fill>
      </div>
      <h3 class="mt-6 text-center blue--text">
        Upload complete
      </h3>
      <div class="text-center mt-8">
        Do not close your browser tab until you have downloaded <br />
        your pathology form(s) and order summary
      </div>

      <v-card-actions class="mt-13">
        <v-btn color="blue" outlined large @click="closeUploadDialog">
          Okay, continue to order summary
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Retry dialog and final alert dialog -->
    <v-card v-else class="d-sm-flex justify-center align-center">
      <v-form ref="form" lazy-validation v-model="valid">
        <v-row style="max-width: 800px;">
          <v-col cols="11" sm="10" class="mx-auto py-10">
            <div class="mb-4">
              <h3 class="text-h5 text-center" style="word-break: break-word">
                {{ dialogTitle }}
              </h3>
              <div class="subtitle-1 text-center mt-8" v-if="showFinalDialog">
                Please email the following attachments to
                <a href="mailto:VCGSgenomics@vcgs.org.au"
                  >VCGSgenomics@vcgs.org.au</a
                >
                <br />and include the 6 character order ID in the subject of
                your email
              </div>
            </div>

            <div v-if="failedPrevReportFiles.length > 0" class="mt-5 mb-10">
              <UploadRetry
                uploadType="previousReport"
                :failedFiles="failedPrevReportFiles"
                :showRetryDialog="showRetryDialog"
                @updateRetryFiles="updateRetryFiles"
              />
            </div>

            <template v-if="failedConsentFiles.length > 0">
              <UploadRetry
                uploadType="consent"
                :failedFiles="failedConsentFiles"
                :showRetryDialog="showRetryDialog"
                @updateRetryFiles="updateRetryFiles"
              />
            </template>

            <div class="text-center mt-8" v-if="showFinalDialog">
              Do not close your browser tab until you have downloaded <br />
              your pathology form(s) and order summary
            </div>

            <v-card-actions class="mt-14 justify-center">
              <v-btn
                color="blue"
                outlined
                @click="upload"
                v-if="showRetryDialog"
              >
                Upload
              </v-btn>

              <v-btn
                color="indigo darken-4"
                outlined
                large
                @click="closeUploadDialog"
                v-else
              >
                Okay, continue to order summary
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { BIconCheckCircleFill } from "bootstrap-vue";
import UploadRetry from "./UploadRetry.vue";

const RETRY_DIALOG_TITLE =
  "Failed to upload the following attachments. Please try again.";

const FINAL_DIALOG_TITLE =
  "Sorry, we were unable to upload the following attachments";

export default {
  components: {
    BIconCheckCircleFill,
    UploadRetry,
  },

  props: {
    closeUploadDialog: Function,
    uploadTries: Number,
    maxTries: Number,
    failedPrevReportFiles: Array,
    failedConsentFiles: Array,
    uploadingStatus: Boolean,
    completeUpload: Boolean,
  },
  data() {
    return {
      valid: false,
      showRetryDialog: false,
      showFinalDialog: false,
      retryConsentFiles: [],
      retryPrevReportFiles: [],
    };
  },

  computed: {
    dialogTitle() {
      return this.showRetryDialog ? RETRY_DIALOG_TITLE : FINAL_DIALOG_TITLE;
    },
    showUploadDialog() {
      if (this.completeUpload === null) return false;

      return true;
    },
  },
  watch: {
    uploadTries: {
      handler(newUploadTries) {
        this.showRetryDialog = newUploadTries < this.maxTries;
        this.showFinalDialog = newUploadTries === this.maxTries;
      },
    },
  },
  methods: {
    updateRetryFiles(retryFilesName, newUploadObj) {
      const index = this[retryFilesName].findIndex(
        (ele) => ele.testId === newUploadObj.testId
      );
      if (index !== -1) {
        this[retryFilesName][index] = newUploadObj;
      } else {
        this[retryFilesName].push(newUploadObj);
      }
    },
    upload() {
      this.valid = this.$refs.form.validate();

      if (this.valid)
        this.$emit("retryUpload", {
          retryConsentFiles: this.retryConsentFiles,
          retryPrevReportFiles: this.retryPrevReportFiles,
        });
    },
  },
};
</script>

<style scoped></style>
